import { useTranslation } from 'react-i18next';
import type { ApprovalActionRecord } from '@/types/approvals';
import { formatDateTime } from '@/utils/format';

interface Props {
  actions: ApprovalActionRecord[];
}

const ACTION_COLORS: Record<string, string> = {
  APPROVE: 'bg-emerald-500',
  AUTO_APPROVE: 'bg-emerald-400',
  SLA_AUTO_APPROVE: 'bg-emerald-400',
  REJECT: 'bg-red-500',
  RETURN: 'bg-amber-500',
  REQUEST_CHANGES: 'bg-amber-500',
  DELEGATE: 'bg-blue-500',
  ESCALATE: 'bg-orange-500',
  SLA_ESCALATE: 'bg-orange-500',
  SLA_NOTIFY: 'bg-amber-500',
  SUBMIT: 'bg-slate-500',
  RESUBMIT: 'bg-slate-500',
  CANCEL: 'bg-slate-600',
  COMMENT: 'bg-slate-400',
  SYSTEM_NOTE: 'bg-slate-300',
};

export function ApprovalTimeline({ actions }: Props) {
  const { t } = useTranslation('workflow');

  if (!actions.length) {
    return <p className="text-sm text-slate-500">{t('timeline.empty')}</p>;
  }

  return (
    <ol className="relative space-y-4 border-l border-slate-200 pl-4 dark:border-slate-700">
      {actions.map((action) => (
        <li key={action.id} className="relative">
          <span
            className={`absolute -left-[1.35rem] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-slate-900 ${
              ACTION_COLORS[action.action] ?? 'bg-slate-400'
            }`}
          />
          <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/50">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {t(`timeline.actions.${action.action}`, { defaultValue: action.action })}
                {action.stepName ? ` · ${action.stepName}` : ''}
              </span>
              <time className="text-xs text-slate-500">
                {action.actedAt ? formatDateTime(action.actedAt, { showTimezone: true }) : ''}
              </time>
            </div>
            {action.action === 'DELEGATE' ? (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {t('timeline.delegatedTo')}{' '}
                <span className="font-medium text-slate-800 dark:text-slate-100">
                  {action.delegateToName ??
                    (action.delegateToUserId != null
                      ? t('timeline.userRef', { id: action.delegateToUserId })
                      : t('timeline.unknownUser'))}
                </span>
              </p>
            ) : null}
            {action.comments ? (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{action.comments}</p>
            ) : null}
            {action.actorName || action.actorUserId ? (
              <p className="mt-1 text-xs text-slate-500">
                {action.action === 'DELEGATE' ? `${t('timeline.by')} ` : ''}
                {action.actorName ?? t('timeline.userRef', { id: action.actorUserId })}
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
